import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { NoticeCampaignRecipientListTable } from '@/components/venue/NoticeCampaignRecipientListTable'
import { NoticeCampaignResultSummary } from '@/components/venue/NoticeCampaignResultSummary'
import { useCampaignDetail } from '@/hooks/useCampaignDetail'
import { useCampaignRecipients } from '@/hooks/useCampaignRecipients'
import { useCampaignResult } from '@/hooks/useCampaignResult'
import { useSendCampaign } from '@/hooks/useSendCampaign'
import { toApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils/cn'
import type { RecipientReadFilter } from '@/lib/venue/campaignViewModel'
import { useVenueContext } from '@/routes/venueContext'

type DetailTab = 'RESULT' | 'RECIPIENTS'

/**
 * 공지·홍보 상세 — 'notices/:campaignId'(설계 §2). 발송 결과 + 대상별 읽음 목록 2탭.
 *
 * 🚨 이 화면은 조회 전용이다 — 수정·취소 버튼을 만들지 않는다(요구사항 "발송 완료 공지에는 수정·취소
 * 버튼을 노출하지 않는다"를 애초에 그 버튼을 만들지 않는 방식으로 지킨다).
 * 🚨 `PROMOTION` 캠페인도 조회한다 — 이 화면이 쓰지 못하는 것은 작성뿐이다(설계 §자율 결정 2).
 * 🚨 `SENDING`→`SENT` 전이는 폴링하지 않는다(설계 §자율 결정 4) — 진입 시 1회 조회하고, 필요하면
 * 사용자가 새로고침(`ErrorState`의 `onRetry`와 동일 수준의 수동 재조회)한다.
 */
export function VenueNoticeDetailPage() {
  const { venueId } = useVenueContext()
  const params = useParams<{ campaignId: string }>()
  const campaignId = Number(params.campaignId)

  const [tab, setTab] = useState<DetailTab>('RESULT')
  const [readFilter, setReadFilter] = useState<RecipientReadFilter>('ALL')
  const [resendError, setResendError] = useState<string | null>(null)

  const detailQuery = useCampaignDetail(venueId, campaignId)
  const resultQuery = useCampaignResult(venueId, campaignId)
  const recipientsQuery = useCampaignRecipients(venueId, campaignId, readFilter, tab === 'RECIPIENTS')
  const resendMutation = useSendCampaign(venueId)

  const recipientPages = recipientsQuery.data?.pages ?? []
  const recipients = recipientPages.flatMap((page) => page.recipients)

  const isLoading = detailQuery.isLoading || resultQuery.isLoading
  const isError = detailQuery.isError || resultQuery.isError

  /**
   * `FAILED`(발송 중 인스턴스가 죽어 회수된 건, #731 §7) + `PROMOTION`만 — 백엔드가 `DRAFT`·
   * `SCHEDULED`·`FAILED`에서 `POST send` 재호출을 허용하므로(#731 §6) 전체 편집 화면 대신 버튼
   * 하나로 재시도한다(설계 §자율 결정 6). `NOTICE`는 무료·시간대 제한 대상이 아니라 실패 재시도
   * UX 자체를 이 태스크가 새로 만들 필요가 없다.
   */
  const canResend = detailQuery.data?.summary.type === 'PROMOTION' && detailQuery.data.summary.status === 'FAILED'

  async function handleResend() {
    setResendError(null)
    try {
      await resendMutation.mutateAsync(campaignId)
      void detailQuery.refetch()
      void resultQuery.refetch()
    } catch (error) {
      setResendError(toApiError(error).message)
    }
  }

  return (
    <>
      <PageHeader
        title="공지·홍보 상세"
        description={
          <Link to={`/venues/${venueId}/notices/history`} className="text-brand-500 underline">
            발송 이력으로 돌아가기
          </Link>
        }
      />

      {isLoading ? <LoadingState rows={4} label="상세를 불러오는 중" /> : null}

      {isError ? (
        <ErrorState
          error={detailQuery.error ?? resultQuery.error}
          onRetry={() => {
            void detailQuery.refetch()
            void resultQuery.refetch()
          }}
        />
      ) : null}

      {!isLoading && !isError && detailQuery.data && resultQuery.data ? (
        <div className="space-y-4">
          {canResend ? (
            <Alert
              tone="warning"
              title="발송이 실패했습니다"
              action={
                <Button type="button" disabled={resendMutation.isPending} onClick={() => void handleResend()}>
                  {resendMutation.isPending ? '재발송 중…' : '재발송'}
                </Button>
              }
            >
              <p>{resendError ?? detailQuery.data.statusReason ?? '발송 처리 중 문제가 발생했습니다. 다시 시도해 주세요.'}</p>
            </Alert>
          ) : null}

          <div role="tablist" aria-label="상세 탭" className="flex gap-2">
            {(
              [
                { value: 'RESULT', label: '발송 결과' },
                { value: 'RECIPIENTS', label: '대상별 읽음 목록' },
              ] as { value: DetailTab; label: string }[]
            ).map((option) => {
              const selected = tab === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => {
                    setTab(option.value)
                  }}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                    selected
                      ? 'border-brand-500 bg-brand-50 text-brand-500'
                      : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                  )}
                >
                  {option.label}
                </button>
              )
            })}
          </div>

          {tab === 'RESULT' ? (
            <NoticeCampaignResultSummary detail={detailQuery.data} result={resultQuery.data} />
          ) : (
            <>
              {recipientsQuery.isLoading ? <LoadingState rows={3} label="대상별 읽음 목록을 불러오는 중" /> : null}
              {recipientsQuery.isError ? <ErrorState error={recipientsQuery.error} /> : null}
              {!recipientsQuery.isLoading && !recipientsQuery.isError ? (
                <NoticeCampaignRecipientListTable
                  items={recipients}
                  readFilter={readFilter}
                  onReadFilterChange={setReadFilter}
                  hasNextPage={recipientsQuery.hasNextPage ?? false}
                  isFetchingNextPage={recipientsQuery.isFetchingNextPage}
                  onLoadMore={() => {
                    void recipientsQuery.fetchNextPage()
                  }}
                />
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </>
  )
}
